import { useRef, useState, useCallback, useMemo, type ReactNode } from 'react';
import { clsx } from 'clsx';
import { X } from 'lucide-react';
import { isValidEmail, splitAddresses, normalizeAddress, MAX_REPLY_RECIPIENTS } from './replyRecipients';
import { TeammateList, type MentionUser } from './MentionPicker';

/** Max teammate suggestions shown under a recipient row. */
const MAX_SUGGESTIONS = 8;

interface RecipientInputProps {
  /** Row label, e.g. "To" or "Cc". Also names the input for screen readers. */
  label: string;
  value: string[];
  onChange: (next: string[]) => void;
  /** Addresses already on the other field — adding one here is a no-op. */
  exclude?: string[];
  disabled?: boolean;
  placeholder?: string;
  /** Rendered at the end of the row (e.g. the "Cc" toggle on the To row). */
  trailing?: ReactNode;
  /**
   * Organisation teammates offered as the agent types (matched on name or
   * email). Any other address can still be typed freely.
   */
  suggestions?: MentionUser[];
}

/**
 * One recipient row of the reply composer: chips plus a free-text input.
 * Enter, Tab, comma, semicolon, paste and blur commit what was typed. Invalid
 * addresses stay visible as flagged chips so the agent can see and remove them;
 * the composer refuses to send while any remain.
 */
export function RecipientInput({
  label,
  value,
  onChange,
  exclude = [],
  disabled = false,
  placeholder,
  trailing,
  suggestions = [],
}: RecipientInputProps) {
  const [draft, setDraft] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  // Escape hides the list until the agent types again.
  const [dismissed, setDismissed] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const isAtMax = value.length >= MAX_REPLY_RECIPIENTS;

  // Teammates matching what's being typed, minus anyone already on either row.
  const matches = useMemo(() => {
    const q = draft.trim().toLowerCase();
    if (!q || dismissed) return [];
    const taken = new Set([...value, ...exclude]);
    return suggestions
      .filter((u) => !!u.email && !taken.has(normalizeAddress(u.email)))
      .filter((u) => u.name.toLowerCase().includes(q) || (u.email ?? '').toLowerCase().includes(q))
      .slice(0, MAX_SUGGESTIONS);
  }, [draft, dismissed, suggestions, value, exclude]);
  const showMatches = matches.length > 0 && !disabled;
  const listId = `recipient-${label}-suggestions`;

  const commit = useCallback(
    (text: string) => {
      const incoming = splitAddresses(text);
      if (incoming.length === 0) {
        setDraft('');
        return;
      }
      const taken = new Set([...value, ...exclude]);
      const next = [...value];
      for (const address of incoming) {
        if (taken.has(address) || next.length >= MAX_REPLY_RECIPIENTS) continue;
        taken.add(address);
        next.push(address);
      }
      if (next.length !== value.length) onChange(next);
      setDraft('');
    },
    [value, exclude, onChange],
  );

  const remove = (address: string) => {
    if (disabled) return;
    onChange(value.filter((a) => a !== address));
  };

  const pickSuggestion = (user: MentionUser) => {
    if (!user.email) return;
    commit(user.email);
    setActiveIndex(0);
    inputRef.current?.focus();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // While teammate suggestions are showing, arrows move through them and
    // Enter/Tab picks the highlighted one. Comma/semicolon still commit what
    // was typed, so a free-form address is never hijacked.
    if (showMatches) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActiveIndex((i) => (i + 1) % matches.length);
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActiveIndex((i) => (i - 1 + matches.length) % matches.length);
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        setDismissed(true);
        return;
      }
      if ((e.key === 'Enter' && !(e.metaKey || e.ctrlKey)) || e.key === 'Tab') {
        e.preventDefault();
        pickSuggestion(matches[activeIndex] ?? matches[0]);
        return;
      }
    }
    if (e.key === 'Enter' || e.key === ',' || e.key === ';' || (e.key === 'Tab' && draft.trim())) {
      // Ctrl/Cmd+Enter belongs to the editor's send shortcut, not here.
      if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) return;
      e.preventDefault();
      commit(draft);
    } else if (e.key === 'Backspace' && !draft && value.length > 0) {
      remove(value[value.length - 1]);
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const text = e.clipboardData.getData('text');
    if (/[\s,;]/.test(text.trim())) {
      e.preventDefault();
      commit(`${draft} ${text}`);
    }
  };

  const invalid = value.filter((a) => !isValidEmail(a));

  return (
    <div className="border-b border-[var(--co-line-subtle)] px-[14px] py-[6px]">
      <div className="flex items-start gap-3">
        <label
          htmlFor={`recipient-${label}`}
          className="w-[22px] flex-none pt-[6px] font-mono text-[10.5px] uppercase leading-none tracking-[.1em] text-[var(--co-ink-4)]"
        >
          {label}
        </label>
        <div
          className="relative flex min-w-0 flex-1 flex-wrap items-center gap-[6px]"
          onClick={() => inputRef.current?.focus()}
        >
          {value.map((address) => {
            const bad = !isValidEmail(address);
            return (
              <span
                key={address}
                data-testid="recipient-chip"
                data-invalid={bad || undefined}
                title={bad ? `"${address}" is not a valid email address` : address}
                className={clsx(
                  'co-tile flex max-w-full items-center gap-1 !px-[6px] !py-[2px]',
                  bad && '!border-[var(--co-burgundy)] bg-[var(--co-burgundy-wash)]',
                )}
              >
                <span
                  className={clsx(
                    'truncate font-mono text-[11px]',
                    bad ? 'text-[var(--co-burgundy)]' : 'text-[var(--co-ink-2)]',
                  )}
                >
                  {address}
                </span>
                {!disabled && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      remove(address);
                    }}
                    className="flex h-4 w-4 flex-none items-center justify-center text-[var(--co-ink-4)] transition-colors duration-[120ms] hover:text-[var(--co-burgundy)]"
                    aria-label={`Remove ${address}`}
                  >
                    <X className="h-3 w-3" />
                  </button>
                )}
              </span>
            );
          })}
          {!isAtMax && (
            <input
              ref={inputRef}
              id={`recipient-${label}`}
              type="text"
              inputMode="email"
              autoComplete="off"
              spellCheck={false}
              value={draft}
              onChange={(e) => {
                setDraft(e.target.value);
                setDismissed(false);
                setActiveIndex(0);
              }}
              aria-controls={showMatches ? listId : undefined}
              aria-autocomplete="list"
              onKeyDown={handleKeyDown}
              onPaste={handlePaste}
              onBlur={() => commit(draft)}
              disabled={disabled}
              placeholder={value.length === 0 ? placeholder : ''}
              aria-label={`${label} recipients`}
              aria-invalid={invalid.length > 0 || undefined}
              className="min-w-[140px] flex-1 border-0 bg-transparent py-[3px] text-[12.5px] text-[var(--co-ink)] outline-none placeholder:text-[var(--co-ink-4)] disabled:cursor-not-allowed"
            />
          )}
          {showMatches && (
            <TeammateList
              id={listId}
              heading="Teammates"
              items={matches}
              activeIndex={activeIndex}
              onHover={setActiveIndex}
              onSelect={pickSuggestion}
              // Opens upward: the composer is docked at the bottom of the
              // page, same as the @mention list.
              className="bottom-full left-0 mb-1"
            />
          )}
        </div>
        {trailing}
      </div>
      {invalid.length > 0 && (
        <p role="alert" className="mt-1 pl-[34px] text-[11.5px] text-[var(--co-burgundy)]">
          {invalid.length === 1
            ? `"${invalid[0]}" is not a valid email address.`
            : `${invalid.length} addresses are not valid. Remove or correct them.`}
        </p>
      )}
      {isAtMax && (
        <p className="mt-1 pl-[34px] text-[11.5px] text-[var(--co-ink-4)]">
          Maximum of {MAX_REPLY_RECIPIENTS} recipients reached.
        </p>
      )}
    </div>
  );
}

export default RecipientInput;
